// 加工指示サンプル情報パネルの行・列・PDTC連動をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiSampleInfoService(deps) {
    const {
        AGRef,
        CELLTYPE,
        DicVal,
        GridApi,
        GridData,
        KAKOUSIJI_SAMPLE_COMMENT_FIELD_ID,
        KAKOUSIJI_SAMPLE_DETAIL_FIELD_IDS,
        KAKOUSIJI_SAMPLE_FIELD_LABELS,
        KAKOUSIJI_SAMPLE_HEADER_FIELD_IDS,
        KAKOUSIJI_SAMPLE_INFO_PANEL_TYPES,
        KAKOUSIJI_SAMPLE_KBN_INITIAL_VALUES,
        KAKOUSIJI_SAMPLE_PARENT_FIELD_IDS,
        SetGridData,
        ValueChanged,
        activeKakousijiSummaryItem,
        buildDerivedPdtcDetails,
        columninfo,
        columns,
        comparePdtcDetailOrderValue,
        entrydataRef,
        fnc,
        getKakousijiEntryCache,
        gridRefFallbackKey,
        gridlist,
        initialPdtcDetail220Ref,
        isEmpty,
        kakousijiSampleRenderedPanelRefs,
        kakousijiSampleSyncRetryTimersRef,
        panel,
        pdtcDetailPanel,
        resolveDetailRowValue,
        setEntryGridRows,
        setSectionEditRenderVersion,
        textfields,
        textfieldsall
    } = deps;

    function isKakousijiSampleInfoType(type) {
        return KAKOUSIJI_SAMPLE_INFO_PANEL_TYPES.includes(String(type ?? '').trim());
    }
    function isKakousijiSampleInfoPanel(panelInfo) {
        if (isEmpty(panelInfo)) return false;
        return isKakousijiSampleInfoType(DicVal(panelInfo, 'type', ''));
    }
    function kakousijiSampleKbnInitialValue(type) {
        return DicVal(KAKOUSIJI_SAMPLE_KBN_INITIAL_VALUES, String(type ?? '').trim(), '1');
    }
    function kakousijiSampleParentFieldIds() {
        return [...KAKOUSIJI_SAMPLE_HEADER_FIELD_IDS, KAKOUSIJI_SAMPLE_COMMENT_FIELD_ID];
    }
    function isKakousijiSampleNestedRow(row) {
        return !isEmpty(row) && Array.isArray(DicVal(row, 'SSDSPM', null));
    }
    function resolveKakousijiSampleSsdNo(source) {
        const row = isEmpty(source) ? {} : source;
        const fromRow = String(DicVal(row, 'SSD_NO', DicVal(row, 'SSD_SIJINO', ''))).trim();
        if (fromRow !== '') return fromRow;
        const fromEntry = String(DicVal(entrydataRef.current, 'SSD_NO', DicVal(entrydataRef.current, 'SSD_SIJINO', ''))).trim();
        if (fromEntry !== '') return fromEntry;
        return String(DicVal(activeKakousijiSummaryItem(), 'code', '')).trim();
    }
    function resolveKakousijiSamplePanelSsdNo(panelInfo, row) {
        const entryKey = String(DicVal(panelInfo, 'sampleEntryKey', '')).trim();
        if (entryKey !== '') {
            const cache = getKakousijiEntryCache(entryKey);
            const cacheData = DicVal(cache, 'data', {});
            const fromCache = String(DicVal(cacheData, 'SSD_NO', DicVal(cacheData, 'SSD_SIJINO', ''))).trim();
            if (fromCache !== '') return fromCache;
        }
        return resolveKakousijiSampleSsdNo(row);
    }
    function applyKakousijiSampleParentSsdNo(row, ssdNo) {
        const next = isEmpty(row) ? {} : { ...row };
        const parentSsdNo = String(ssdNo ?? '').trim();
        if (parentSsdNo !== '') next.SSDSP_NO = parentSsdNo;
        else delete next.SSDSP_NO;
        return next;
    }
    function pickKakousijiSampleParentValues(row, type) {
        const source = isEmpty(row) ? {} : row;
        const parent = {};
        kakousijiSampleParentFieldIds().forEach((fieldId) => {
            parent[fieldId] = DicVal(source, fieldId, '');
        });
        parent.SSDSP_KBN = kakousijiSampleKbnInitialValue(type);
        return applyKakousijiSampleParentSsdNo(parent, resolveKakousijiSampleSsdNo(source));
    }
    function pickKakousijiSampleDetailValues(row) {
        const source = isEmpty(row) ? {} : row;
        return {
            SSDSPM_COLORCD: DicVal(source, 'SSDSPM_COLORCD', ''),
            SSDSPM_SIZECD: DicVal(source, 'SSDSPM_SIZECD', ''),
            SSDSPM_SURYO: DicVal(source, 'SSDSPM_SURYO', '')
        };
    }
    function hasKakousijiSampleDetailValues(row) {
        const detail = pickKakousijiSampleDetailValues(row);
        return KAKOUSIJI_SAMPLE_DETAIL_FIELD_IDS.some((fieldId) => String(DicVal(detail, fieldId, '') ?? '').trim() !== '');
    }
    function normalizeKakousijiSampleDetailRowsForDisplay(type, rows) {
        const sourceRows = Array.isArray(rows) ? rows : [];
        const displayRows = [];
        sourceRows.forEach((row) => {
            if (isKakousijiSampleNestedRow(row)) {
                const parent = pickKakousijiSampleParentValues(row, type);
                const details = Array.isArray(DicVal(row, 'SSDSPM', [])) ? DicVal(row, 'SSDSPM', []) : [];
                if (details.length === 0) {
                    displayRows.push({ ...parent });
                    return;
                }
                details.forEach((detail) => {
                    displayRows.push({ ...parent, ...pickKakousijiSampleDetailValues(detail) });
                });
                return;
            }
            displayRows.push({ ...(row || {}) });
        });
        return displayRows.map((row, index) => applyKakousijiSampleParentSsdNo({ ...row, rowid: String(DicVal(row, 'rowid', index + 1)) }, resolveKakousijiSampleSsdNo(row)));
    }
    function buildKakousijiSampleNestedRows(type, rows) {
        const sourceRows = Array.isArray(rows) ? rows : [];
        if (sourceRows.some((row) => isKakousijiSampleNestedRow(row))) {
            return sourceRows.map((row) => {
                const parent = pickKakousijiSampleParentValues(row, type);
                const details = Array.isArray(DicVal(row, 'SSDSPM', [])) ? DicVal(row, 'SSDSPM', []) : [];
                parent.SSDSPM = details.map((detail) => pickKakousijiSampleDetailValues(detail));
                return parent;
            });
        }
        const parentSource = sourceRows.find((row) => !isEmpty(row)) || {};
        const parent = pickKakousijiSampleParentValues(parentSource, type);
        parent.SSDSPM = sourceRows
            .filter((row) => hasKakousijiSampleDetailValues(row))
            .map((row) => pickKakousijiSampleDetailValues(row));
        return [parent];
    }
    function normalizeKakousijiSampleDetailForDisplayByType(type, rows) {
        return isKakousijiSampleInfoType(type) ? normalizeKakousijiSampleDetailRowsForDisplay(type, rows) : rows;
    }
    function normalizeKakousijiSampleDetailForStoreByType(type, rows) {
        return isKakousijiSampleInfoType(type) ? buildKakousijiSampleNestedRows(type, rows) : rows;
    }
    function registerKakousijiSampleRenderedPanel(panelInfo) {
        if (!isKakousijiSampleInfoPanel(panelInfo)) return panelInfo;
        const type = String(DicVal(panelInfo, 'type', '')).trim();
        kakousijiSampleRenderedPanelRefs.current[type] = panelInfo;
        return panelInfo;
    }
    function uniqueKakousijiSamplePanels(items) {
        const used = new Set();
        return (Array.isArray(items) ? items : []).filter((item) => {
            if (!isKakousijiSampleInfoPanel(item)) return false;
            const key = gridRefFallbackKey(item);
            if (used.has(key)) return false;
            used.add(key);
            return true;
        });
    }
    function kakousijiSampleSyncPanels() {
        return uniqueKakousijiSamplePanels([
            ...Object.values(kakousijiSampleRenderedPanelRefs.current || {}),
            ...(Array.isArray(panel) ? panel : []),
            ...(Array.isArray(gridlist) ? gridlist : [])
        ]);
    }
    function uniqueFieldsByColumnId(fields) {
        const used = new Set();
        return (Array.isArray(fields) ? fields : []).filter((field) => {
            const columnid = String(DicVal(field, 'columnid', DicVal(field, 'field', ''))).trim().toUpperCase();
            if (columnid === '' || used.has(columnid)) return false;
            used.add(columnid);
            return true;
        });
    }
    function kakousijiSampleFieldSource(panelInfo) {
        const type = String(DicVal(panelInfo, 'type', '')).trim();
        return uniqueFieldsByColumnId([textfieldsall, textfields, columninfo, columns]
            .flatMap((source) => Array.isArray(source) ? source : [])
            .filter((field) => String(DicVal(field, 'type', '')).trim() === type));
    }
    function findKakousijiSampleField(panelInfo, columnid) {
        const normalized = String(columnid).trim().toUpperCase();
        const field = kakousijiSampleFieldSource(panelInfo).find((item) => String(DicVal(item, 'columnid', DicVal(item, 'field', ''))).trim().toUpperCase() === normalized);
        if (!isEmpty(field)) return field;
        if (KAKOUSIJI_SAMPLE_PARENT_FIELD_IDS.includes(normalized)) {
            const type = String(DicVal(panelInfo, 'type', '')).trim();
            const label = DicVal(KAKOUSIJI_SAMPLE_FIELD_LABELS, normalized, normalized);
            return { type: type, columnid: normalized, field: normalized, columnnm: label, headerName: label, datatype: CELLTYPE.TEXT, length: 0, format: '', locked: true, unchange: true, required: false, hide: false, hidden: '0' };
        }
        return null;
    }
    function kakousijiSampleHeaderFields(panelInfo) {
        return KAKOUSIJI_SAMPLE_HEADER_FIELD_IDS
            .map((columnid) => {
                const field = findKakousijiSampleField(panelInfo, columnid);
                if (isEmpty(field)) return null;
                const normalized = String(columnid).trim().toUpperCase();
                if (KAKOUSIJI_SAMPLE_PARENT_FIELD_IDS.includes(normalized)) {
                    return { ...field, locked: true, unchange: true, primary: false, required: false };
                }
                return field;
            })
            .filter((field) => !isEmpty(field));
    }
    function kakousijiSampleDetailColumnInfo(fields, panelInfo = null) {
        const panelType = String(DicVal(panelInfo, 'type', '')).trim();
        const source = Array.isArray(fields) ? fields : [];
        const fallbackSource = [source, columninfo, columns, textfieldsall, textfields]
            .flatMap((items) => Array.isArray(items) ? items : []);
        const fieldColumnId = (field) => String(DicVal(field, 'columnid', DicVal(field, 'field', ''))).trim().toUpperCase();
        const fieldType = (field) => String(DicVal(field, 'type', '')).trim();
        return KAKOUSIJI_SAMPLE_DETAIL_FIELD_IDS
            .map((targetId) => {
                const panelField = source.find((field) => fieldType(field) === panelType && fieldColumnId(field) === targetId);
                if (!isEmpty(panelField)) return panelField;
                const fallbackPanelField = fallbackSource.find((field) => fieldType(field) === panelType && fieldColumnId(field) === targetId);
                if (!isEmpty(fallbackPanelField)) return fallbackPanelField;
                const sample140Field = fallbackSource.find((field) => fieldType(field) === '140' && fieldColumnId(field) === targetId);
                if (!isEmpty(sample140Field)) return { ...sample140Field, type: panelType };
                return null;
            })
            .filter((field) => !isEmpty(field))
            .map((field) => {
                const columnid = fieldColumnId(field);
                const label = DicVal(KAKOUSIJI_SAMPLE_FIELD_LABELS, columnid, DicVal(field, 'columnnm', DicVal(field, 'headerName', '')));
                return { ...field, type: panelType, columnid: columnid, field: columnid, columnnm: label, headerName: label, required: false, hide: false, hidden: '0' };
            });
    }
    function ensureKakousijiSampleInfoRows(panelInfo) {
        let rows = GridData(panelInfo);
        if (!Array.isArray(rows)) rows = [];
        if (rows.length === 0) rows = [{ rowid: '0' }];
        if (isEmpty(rows[0]) || typeof rows[0] !== 'object') rows[0] = { rowid: '0' };
        if (isEmpty(DicVal(rows[0], 'rowid', ''))) rows[0].rowid = '0';
        const sampleKbnInitialValue = DicVal(KAKOUSIJI_SAMPLE_KBN_INITIAL_VALUES, String(DicVal(panelInfo, 'type', '')).trim(), '1');
        if (isEmpty(DicVal(rows[0], 'SSDSP_KBN', ''))) rows[0].SSDSP_KBN = sampleKbnInitialValue;
        const parentSsdNo = resolveKakousijiSamplePanelSsdNo(panelInfo, rows[0]);
        rows = rows.map((row) => applyKakousijiSampleParentSsdNo(row, parentSsdNo));
        SetGridData(panelInfo, rows);
        return rows;
    }
    function syncKakousijiSampleHeaderValue(panelInfo, columnid, value) {
        const normalizedColumnId = String(columnid).trim().toUpperCase();
        const baseRows = ensureKakousijiSampleInfoRows(panelInfo);
        const parentSsdNo = resolveKakousijiSamplePanelSsdNo(panelInfo, DicVal(baseRows, 0, {}));
        const nextValue = KAKOUSIJI_SAMPLE_PARENT_FIELD_IDS.includes(normalizedColumnId) ? parentSsdNo : value;
        const rows = baseRows.map((row, index) => {
            const next = { ...(row || {}) };
            if (index === 0 || !KAKOUSIJI_SAMPLE_DETAIL_FIELD_IDS.some((id) => !isEmpty(DicVal(next, id, '')))) {
                next[normalizedColumnId] = nextValue;
            }
            return KAKOUSIJI_SAMPLE_PARENT_FIELD_IDS.includes(normalizedColumnId) ? applyKakousijiSampleParentSsdNo(next, parentSsdNo) : next;
        });
        SetGridData(panelInfo, rows);
    }
    function handleKakousijiSampleInfoValueChanged(panelInfo, id, value, edit, options) {
        const normalizedId = String(id).trim().toUpperCase();
        syncKakousijiSampleHeaderValue(panelInfo, normalizedId, value);
        if (KAKOUSIJI_SAMPLE_PARENT_FIELD_IDS.includes(normalizedId)) return;
        ValueChanged(normalizedId, value, edit, { ...(isEmpty(options) ? {} : options), targetData: DicVal(GridData(panelInfo), 0, null), panel: panelInfo });
    }
    function kakousijiSampleSuryoTotal(rows) {
        const toNum = (value) => {
            const num = Number(String(value ?? '').replace(/,/g, ''));
            return Number.isFinite(num) ? num : 0;
        };
        return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + toNum(DicVal(row, 'SSDSPM_SURYO', '')), 0);
    }
    function kakousijiSampleFieldLabel(field) {
        const columnid = String(DicVal(field, 'columnid', '')).trim().toUpperCase();
        return DicVal(KAKOUSIJI_SAMPLE_FIELD_LABELS, columnid, String(DicVal(field, 'columnnm', '')).replace(/^[#★\s　]+/, ''));
    }
    function buildKakousijiSampleDetailKey(colorCode, sizeCode) {
        return `${String(isEmpty(colorCode) ? '' : colorCode).trim()}__${String(isEmpty(sizeCode) ? '' : sizeCode).trim()}`;
    }
    function uniqueKakousijiSampleCodeRows(rows, columnid, orderColumnid) {
        const used = new Set();
        return (Array.isArray(rows) ? rows : [])
            .map((row) => ({ row: row || {}, code: String(resolveDetailRowValue(row, columnid, '')).trim() }))
            .filter((item) => {
                if (item.code === '' || used.has(item.code)) return false;
                used.add(item.code);
                return true;
            })
            .sort((left, right) => comparePdtcDetailOrderValue(
                resolveDetailRowValue(left.row, orderColumnid, left.code),
                resolveDetailRowValue(right.row, orderColumnid, right.code)
            ));
    }
    function hasPdtcRowsForSampleCombination(rows, type) {
        if (!Array.isArray(rows) || rows.length === 0) return false;
        const codeColumnId = String(type).trim() === '200' ? 'PDTC_COLORCD' : 'PDTC_SIZECD';
        return rows.some((row) => String(resolveDetailRowValue(row, codeColumnId, '')).trim() !== '');
    }
    function pdtcRowsForSampleCombination(type) {
        const panelInfo = pdtcDetailPanel(type);
        const panelRows = isEmpty(panelInfo) ? [] : GridData(panelInfo);
        if (hasPdtcRowsForSampleCombination(panelRows, type)) return panelRows;
        const gridApi = isEmpty(panelInfo) ? null : GridApi(panelInfo);
        if (!isEmpty(gridApi) && !isEmpty(AGRef.current) && typeof AGRef.current.GetDatasource === 'function') {
            const apiRows = AGRef.current.GetDatasource(gridApi);
            if (hasPdtcRowsForSampleCombination(apiRows, type)) {
                SetGridData(panelInfo, apiRows);
                return apiRows;
            }
        }
        const skuRows = Array.isArray(initialPdtcDetail220Ref.current) ? initialPdtcDetail220Ref.current : [];
        if (type === '200') return buildDerivedPdtcDetails({ '220': skuRows }, '200', 'PDTC_COLORCD', 'PDTC_COLORHYOJIJUN');
        if (type === '210') return buildDerivedPdtcDetails({ '220': skuRows }, '210', 'PDTC_SIZECD', 'PDTC_SIZEHYOJIJUN');
        return [];
    }
    function kakousijiSampleCombinationSourceRows() {
        const colorRows = uniqueKakousijiSampleCodeRows(pdtcRowsForSampleCombination('200'), 'PDTC_COLORCD', 'PDTC_COLORHYOJIJUN');
        const sizeRows = uniqueKakousijiSampleCodeRows(pdtcRowsForSampleCombination('210'), 'PDTC_SIZECD', 'PDTC_SIZEHYOJIJUN');
        return { colorRows, sizeRows };
    }
    function hasKakousijiSampleCombinationSourceRows(sourceRows) {
        return !isEmpty(sourceRows) && sourceRows.colorRows.length !== 0 && sourceRows.sizeRows.length !== 0;
    }
    function buildKakousijiSampleCombinationRows(panelInfo, sourceRows = null) {
        const sampleKbn = DicVal(KAKOUSIJI_SAMPLE_KBN_INITIAL_VALUES, String(DicVal(panelInfo, 'type', '')).trim(), '1');
        const combinationSource = isEmpty(sourceRows) ? kakousijiSampleCombinationSourceRows() : sourceRows;
        const colorRows = combinationSource.colorRows;
        const sizeRows = combinationSource.sizeRows;
        if (colorRows.length === 0 || sizeRows.length === 0) return Array.isArray(GridData(panelInfo)) ? GridData(panelInfo) : [];

        const existingMap = new Map();
        (Array.isArray(GridData(panelInfo)) ? GridData(panelInfo) : []).forEach((row) => {
            const colorCode = String(DicVal(row, 'SSDSPM_COLORCD', '')).trim();
            const sizeCode = String(DicVal(row, 'SSDSPM_SIZECD', '')).trim();
            if (colorCode === '' || sizeCode === '') return;
            existingMap.set(buildKakousijiSampleDetailKey(colorCode, sizeCode), row);
        });

        const rows = [];
        colorRows.forEach((colorItem) => {
            sizeRows.forEach((sizeItem) => {
                const key = buildKakousijiSampleDetailKey(colorItem.code, sizeItem.code);
                const existing = existingMap.get(key) || {};
                rows.push({
                    ...existing,
                    rowid: String(DicVal(existing, 'rowid', `140_${rows.length + 1}`)),
                    SSDSP_KBN: sampleKbn,
                    SSDSPM_COLORCD: colorItem.code,
                    SSDSPM_SIZECD: sizeItem.code,
                    SSDSPM_SURYO: DicVal(existing, 'SSDSPM_SURYO', '')
                });
            });
        });
        return rows.map((row, index) => ({ ...row, rowid: String(index + 1) }));
    }
    function sameKakousijiSampleDetailRows(leftRows, rightRows) {
        const left = Array.isArray(leftRows) ? leftRows : [];
        const right = Array.isArray(rightRows) ? rightRows : [];
        if (left.length !== right.length) return false;
        for (let i = 0; i < left.length; i++) {
            if (String(DicVal(left[i], 'SSDSPM_COLORCD', '')) !== String(DicVal(right[i], 'SSDSPM_COLORCD', ''))) return false;
            if (String(DicVal(left[i], 'SSDSPM_SIZECD', '')) !== String(DicVal(right[i], 'SSDSPM_SIZECD', ''))) return false;
            if (String(DicVal(left[i], 'SSDSPM_SURYO', '')) !== String(DicVal(right[i], 'SSDSPM_SURYO', ''))) return false;
        }
        return true;
    }
    function scheduleKakousijiSampleSyncRetry(panelInfo, remaining = 25) {
        if (!isKakousijiSampleInfoPanel(panelInfo) || remaining <= 0) return;
        const key = gridRefFallbackKey(panelInfo);
        if (!isEmpty(kakousijiSampleSyncRetryTimersRef.current[key])) clearTimeout(kakousijiSampleSyncRetryTimersRef.current[key]);
        kakousijiSampleSyncRetryTimersRef.current[key] = setTimeout(() => {
            kakousijiSampleSyncRetryTimersRef.current[key] = null;
            syncKakousijiSampleDetailRowsFromPdtc(panelInfo, true, remaining - 1);
        }, 120);
    }
    function syncKakousijiSampleDetailRowsFromPdtc(panelInfo, refreshGrid = true, retryRemaining = 0) {
        if (!isKakousijiSampleInfoPanel(panelInfo)) return;
        ensureKakousijiSampleInfoRows(panelInfo);
        const sourceRows = kakousijiSampleCombinationSourceRows();
        if (!hasKakousijiSampleCombinationSourceRows(sourceRows)) {
            if (retryRemaining > 0) scheduleKakousijiSampleSyncRetry(panelInfo, retryRemaining);
            return;
        }
        const currentRows = Array.isArray(GridData(panelInfo)) ? GridData(panelInfo) : [];
        const nextRows = buildKakousijiSampleCombinationRows(panelInfo, sourceRows);
        const sameRows = sameKakousijiSampleDetailRows(currentRows, nextRows);
        if (sameRows) {
            if (refreshGrid && !isEmpty(GridApi(panelInfo))) {
                setEntryGridRows(panelInfo, nextRows);
            } else {
            }
            return;
        }
        if (refreshGrid) {
            setEntryGridRows(panelInfo, nextRows);
            setSectionEditRenderVersion((prev) => prev + 1);
            return;
        }
        SetGridData(panelInfo, nextRows);
    }
    function syncKakousijiSamplePanelsFromPdtc(retryRemaining = 25) {
        const samplePanels = kakousijiSampleSyncPanels();
        samplePanels.forEach((samplePanel) => syncKakousijiSampleDetailRowsFromPdtc(samplePanel, true, retryRemaining));
    }
    function updateKakousijiSampleComment(panelInfo, value) {
        if (isEmpty(entrydataRef.current) || typeof entrydataRef.current !== 'object') entrydataRef.current = {};
        entrydataRef.current[KAKOUSIJI_SAMPLE_COMMENT_FIELD_ID] = value;
        syncKakousijiSampleHeaderValue(panelInfo, KAKOUSIJI_SAMPLE_COMMENT_FIELD_ID, value);
        ValueChanged(KAKOUSIJI_SAMPLE_COMMENT_FIELD_ID, value, true, { panel: panelInfo });
        if (!isEmpty(fnc.current) && typeof fnc.current.modify === 'function') fnc.current.modify(true);
    }

    return {
        isKakousijiSampleInfoType,
        isKakousijiSampleInfoPanel,
        kakousijiSampleKbnInitialValue,
        kakousijiSampleParentFieldIds,
        isKakousijiSampleNestedRow,
        resolveKakousijiSampleSsdNo,
        resolveKakousijiSamplePanelSsdNo,
        applyKakousijiSampleParentSsdNo,
        pickKakousijiSampleParentValues,
        pickKakousijiSampleDetailValues,
        hasKakousijiSampleDetailValues,
        normalizeKakousijiSampleDetailRowsForDisplay,
        buildKakousijiSampleNestedRows,
        normalizeKakousijiSampleDetailForDisplayByType,
        normalizeKakousijiSampleDetailForStoreByType,
        registerKakousijiSampleRenderedPanel,
        uniqueKakousijiSamplePanels,
        kakousijiSampleSyncPanels,
        uniqueFieldsByColumnId,
        kakousijiSampleFieldSource,
        findKakousijiSampleField,
        kakousijiSampleHeaderFields,
        kakousijiSampleDetailColumnInfo,
        ensureKakousijiSampleInfoRows,
        syncKakousijiSampleHeaderValue,
        handleKakousijiSampleInfoValueChanged,
        kakousijiSampleSuryoTotal,
        kakousijiSampleFieldLabel,
        buildKakousijiSampleDetailKey,
        uniqueKakousijiSampleCodeRows,
        hasPdtcRowsForSampleCombination,
        pdtcRowsForSampleCombination,
        kakousijiSampleCombinationSourceRows,
        hasKakousijiSampleCombinationSourceRows,
        buildKakousijiSampleCombinationRows,
        sameKakousijiSampleDetailRows,
        scheduleKakousijiSampleSyncRetry,
        syncKakousijiSampleDetailRowsFromPdtc,
        syncKakousijiSamplePanelsFromPdtc,
        updateKakousijiSampleComment
    };
}
